import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { useOrgAdminDashboard } from "@/hooks/queries/useOrgAdminDashboard";
import { useCreateOrgInvitation } from "@/hooks/queries/useCreateOrgInvitation";
import {
  addPersonSchema,
  ORGANIZATION_ROLE_LABELS,
  USER_TYPE_VALUES,
  type AddPersonValues,
} from "@/routes/org-admin/add-person-schema";
import { contractNotice } from "@/routes/org-admin/contract-notice";
import {
  toCreateInvitationError,
  type InvitationBanner,
} from "@/routes/org-admin/invitation-error";

// Org_admin "Add person" invitation form (Story 14.2, FR75 + UX §13.9).
// Mockup screen-34 governs the section grouping (Identity / Organization /
// Seat & user type), the info banners, and the copy tone; the §13.9 field
// contract OVERRIDES the mockup's drifted controls:
//   - Org role label = a native <select> from a preset list, stored/submitted as
//     the free-form label STRING (FR75 — a display value, never a permission id).
//   - User type = a radio group with EXACTLY two options (new_learner /
//     legacy_certified). The mockup's third "Non-clinician (no seat)" option and
//     its "Seat on activation" control are out of scope and omitted.
// Seats are consumed on invite; the mockup sidebar's "when they accept" copy is
// non-binding (no accept flow in the MVP).
//
// No-seats gate (AC5): the seat pool is read from the org_admin dashboard query;
// when `remaining == 0` the submit is disabled, a `blocked` "No seats available"
// pill is shown, and the screen links to the Request-additional-seats CTA on the
// dashboard (Story 14.7). Errors surface via inline Banner (no toasts), and a
// deterministic SEAT-001 from the POST maps to the same calm no-seats branch.
//
// Contract gate (Story 14.12, AC7): the same pattern for `contract_status !==
// 'active'` — disabled submit, calm banner with the shared copy, link back to
// the dashboard's (un-gated) Request-additional-seats CTA — plus the ORG-010
// mapping in invitation-error.ts, so a refusal that arrives from the SERVER
// (stale cache, direct API call) reads the same as the pre-submit gate.
//
// Tokens only; no pure-white background, no hardcoded hex (CLAUDE.md).

// Radio option copy mirrors the mockup's user-type descriptions, trimmed to the
// two authoritative values. Value stored is the wire user_type; the description
// explains the downstream onboarding route.
const USER_TYPE_OPTIONS: ReadonlyArray<{
  value: (typeof USER_TYPE_VALUES)[number];
  label: string;
  description: string;
}> = [
  {
    value: "new_learner",
    label: "New AMPS learner",
    description: "Routes through the LearnWorlds course, then calibration.",
  },
  {
    value: "legacy_certified",
    label: "Legacy AMPS-certified clinician",
    description: "Bypasses LearnWorlds — goes straight to calibration with web-app credentials.",
  },
];

export function AddPersonScreen() {
  const navigate = useNavigate();
  const [banner, setBanner] = React.useState<InvitationBanner | null>(null);

  // Seat pool (Story 14.1 dashboard query) — the source of the no-seats gate.
  // While it loads we do NOT pre-emptively block; the server SEAT-001 pre-check
  // is the authoritative backstop and maps to the same calm branch.
  const { data: dashboard } = useOrgAdminDashboard();
  const remaining = dashboard?.seat_pool.remaining;
  const noSeats = remaining === 0;

  // Contract gate (Story 14.12), same shape and same reasoning as the no-seats
  // gate above: pre-empt a refusal the backend would answer 403 ORG-010, but
  // treat the SERVER as authoritative — a still-loading dashboard blocks
  // nothing, and a direct POST is caught by the ORG-010 mapping below.
  const contract = contractNotice(
    dashboard?.contract_status,
    dashboard?.contract_end_date,
  );
  // Either gate is sufficient to disable the submit, and both banners may show:
  // an org can simultaneously be full AND out of contract, and hiding one of
  // the two reasons would make the fixed one look like it changed nothing.
  const blocked = noSeats || contract !== null;

  const form = useForm<AddPersonValues>({
    resolver: zodResolver(addPersonSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      workEmail: "",
      organizationRoleLabel: "Clinician",
      department: "",
      hrId: "",
      userType: "new_learner",
    },
  });

  const createInvitation = useCreateOrgInvitation();

  const onSubmit = form.handleSubmit((values) => {
    if (createInvitation.isPending || blocked) return;
    setBanner(null);
    createInvitation.mutate(
      {
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        work_email: values.workEmail.trim(),
        organization_role_label: values.organizationRoleLabel,
        // Optional metadata: omit when blank so the API stores NULL, not "".
        department: values.department.trim() || undefined,
        hr_id: values.hrId.trim() || undefined,
        user_type: values.userType,
      },
      {
        onSuccess: () => {
          // The hook invalidates the dashboard (seat pool + roster); return to it.
          navigate("/org-admin/dashboard");
        },
        onError: (error) => {
          const mapped = toCreateInvitationError(error);
          if (mapped.fieldErrors.firstName) {
            form.setError("firstName", { message: mapped.fieldErrors.firstName });
          }
          if (mapped.fieldErrors.lastName) {
            form.setError("lastName", { message: mapped.fieldErrors.lastName });
          }
          if (mapped.fieldErrors.workEmail) {
            form.setError("workEmail", { message: mapped.fieldErrors.workEmail });
          }
          if (mapped.fieldErrors.organizationRoleLabel) {
            form.setError("organizationRoleLabel", {
              message: mapped.fieldErrors.organizationRoleLabel,
            });
          }
          setBanner(mapped.banner);
        },
      },
    );
  });

  const userTypeError = form.formState.errors.userType?.message;

  // Accessible reason for the disabled submit (the dashboard's
  // add-person-blocked-reason wiring, mirrored): the blocked badges beside the
  // button are the visible reason, so the button points at whichever are
  // rendered — a rotor user hearing "Send invitation, dimmed" gets the why
  // without hunting the surrounding prose.
  const submitBlockedReasonIds =
    [
      noSeats ? "no-seats-blocked-reason" : null,
      contract ? "contract-blocked-reason" : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">Add person</h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Invite a clinician into your organization. The invitation is sent
            automatically — new learners receive a LearnWorlds course link, legacy
            clinicians receive web-app credentials with a forced-password-change
            prompt on first sign-in.
          </p>
        </div>
        <div className="shrink-0">
          <Button asChild variant="secondary">
            <Link to="/org-admin/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </header>

      {/* Seat-pool notice (mockup's top info banner). When the pool is exhausted
          the invitation is blocked here and the copy directs to the 14.7 CTA. */}
      {noSeats ? (
        <Banner variant="info" title="This organization is at seat capacity">
          <div className="flex flex-col gap-2">
            <span>
              This organization has no remaining seats, so new invitations are
              blocked. Ask CITS for more capacity from your dashboard.
            </span>
            <span>
              <Button asChild variant="secondary" size="sm">
                <Link to="/org-admin/dashboard">Request additional seats</Link>
              </Button>
            </span>
          </div>
        </Banner>
      ) : typeof remaining === "number" ? (
        <Banner variant="info" title={`${remaining} seat${remaining === 1 ? "" : "s"} remaining`}>
          Adding a clinician consumes a seat. When the pool is exhausted, use the
          Request additional seats action on your dashboard.
        </Banner>
      ) : null}

      {/* Contract gate (14.12). Stacks with the seat-pool notice above — the
          two are independent reasons, and the link back to the dashboard is
          where the un-gated Request-additional-seats CTA lives. */}
      {contract ? (
        <Banner variant="warn" title={contract.title}>
          <div className="flex flex-col gap-2">
            <span>{contract.body}</span>
            <span>
              <Button asChild variant="secondary" size="sm">
                <Link to="/org-admin/dashboard">Request additional seats</Link>
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {banner ? (
        <Banner variant={banner.variant} title={banner.title}>
          <div className="flex flex-col gap-2">
            <span>{banner.body}</span>
            {banner.seatsCta ? (
              <span>
                <Button asChild variant="secondary" size="sm">
                  <Link to="/org-admin/dashboard">Request additional seats</Link>
                </Button>
              </span>
            ) : null}
          </div>
        </Banner>
      ) : null}

      <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
        {/* Identity ------------------------------------------------------- */}
        <fieldset className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
          <legend className="px-1 text-sm font-semibold text-[color:var(--text-primary)]">
            Identity
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField label="First name" error={form.formState.errors.firstName?.message} required>
              <Input autoComplete="off" placeholder="Given name" {...form.register("firstName")} />
            </FormField>
            <FormField label="Last name" error={form.formState.errors.lastName?.message} required>
              <Input autoComplete="off" placeholder="Family name" {...form.register("lastName")} />
            </FormField>
            <FormField label="Work email" error={form.formState.errors.workEmail?.message} required>
              <Input
                type="email"
                autoComplete="off"
                placeholder="name@organization.org"
                {...form.register("workEmail")}
              />
            </FormField>
          </div>
        </fieldset>

        {/* Organization -------------------------------------------------- */}
        <fieldset className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
          <legend className="px-1 text-sm font-semibold text-[color:var(--text-primary)]">
            Organization
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label="Organization role"
              htmlFor="add-person-org-role"
              error={form.formState.errors.organizationRoleLabel?.message}
              helper="A display label only — every clinician has the same permissions."
              required
            >
              <Controller
                name="organizationRoleLabel"
                control={form.control}
                render={({ field }) => (
                  <SearchableCombobox
                    id="add-person-org-role"
                    options={ORGANIZATION_ROLE_LABELS.map((l) => ({ value: l, label: l }))}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    searchAriaLabel="Organization role"
                    showSearch={false}
                  />
                )}
              />
            </FormField>
            <FormField label="Department" error={form.formState.errors.department?.message}>
              <Input
                autoComplete="off"
                placeholder="e.g. Inpatient OT, Site B"
                {...form.register("department")}
              />
            </FormField>
            <FormField label="HR ID" error={form.formState.errors.hrId?.message}>
              <Input
                autoComplete="off"
                placeholder="Internal reference"
                {...form.register("hrId")}
              />
            </FormField>
          </div>
        </fieldset>

        {/* User type ----------------------------------------------------- */}
        <fieldset className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
          <legend className="px-1 text-sm font-semibold text-[color:var(--text-primary)]">
            User type
          </legend>
          {/* Radio group (§13.9): exactly two options; the chosen value drives the
              backend user_origin and the onboarding route. */}
          <div
            role="radiogroup"
            aria-label="User type"
            aria-required="true"
            aria-invalid={userTypeError ? true : undefined}
            aria-describedby={userTypeError ? "user-type-error" : undefined}
            className="flex flex-col gap-3"
          >
            {USER_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-[color:var(--border-primary)] px-4 py-3 transition-colors focus-within:border-[color:var(--border-brand-alt)] focus-within:shadow-focus has-[:checked]:border-[color:var(--border-brand-alt)] has-[:checked]:bg-[color:var(--bg-brand-primary)]"
              >
                <input
                  type="radio"
                  value={option.value}
                  className="mt-1 h-4 w-4 accent-[color:var(--bg-brand-solid)]"
                  {...form.register("userType")}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-[color:var(--text-primary)]">
                    {option.label}
                  </span>
                  <span className="text-sm text-[color:var(--text-tertiary)]">
                    {option.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {userTypeError ? (
            <p id="user-type-error" role="alert" className="text-sm text-[color:var(--text-error)]">
              {userTypeError}
            </p>
          ) : null}
        </fieldset>

        <div className="mt-1 flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            variant="primary"
            disabled={createInvitation.isPending || blocked}
            aria-describedby={submitBlockedReasonIds}
          >
            {createInvitation.isPending ? "Sending…" : "Send invitation"}
          </Button>
          {noSeats ? (
            <Badge id="no-seats-blocked-reason" variant="blocked">
              No seats available
            </Badge>
          ) : null}
          {contract ? (
            <Badge id="contract-blocked-reason" variant="blocked">
              Contract not active
            </Badge>
          ) : null}
          <Button asChild variant="link">
            <Link to="/org-admin/dashboard">Cancel</Link>
          </Button>
        </div>
      </form>
    </section>
  );
}

export default AddPersonScreen;
